import { ICornerPanelSource } from "../../../responses/cornerPanelSource";
import { IPanelGeneratedDrawingDto } from "../../../responses/panelGeneratedDrawingDto";
import { IPanelGeneratedModelDto, PanelTypeGenerationStatus } from "../../../responses/panelGeneratedModelDto";
import { IPanelSource } from "../../../responses/panelSource";
import { FacadeElement } from "../../../responses/typesManagerCategories";
import { TypesManagerDockingPanel } from "../docking-panels/typesManagerDockingPanel";
import { CornersModelEditor } from "../editors/cornersModelEditor";
import { CustomCornersModelEditor } from "../editors/customCornersModelEditor";
import { CustomPanelsModelEditor } from "../editors/customPanelsModelEditor";
import { CustomZShapedPanelsModelEditor } from "../editors/customZShapedPanelsModelEditor";
import { PanelsModelEditor } from "../editors/panelsModelEditor";
import { PanelType } from "../panels/panelType";
import { hooksToolbarGroupId, measureToolsToolbarGroupId } from "../toolbar/toolbarGroupIds";
import { CategoryType } from "../typesManager/constants/panelTypeEnums";
import { GeneratedPanelsContent } from "../typesManager/generatedPanelsContent";
import { PanelTypeCategories } from "../typesManager/panelTypeCategories";
import { IViewerAggregateSelectionChangedEventPayload } from "../viewer-utils/viewerEventPayloads";
import { ViewerZoomingService } from "../viewer-utils/viewerZoomingService";
import { RequestModelGenerationPayload } from "../eventBus/generatedModelsEventPayloads";
import {
    CornerPanelSelectionChangedEventPayload,
    CustomCornerSelectionChangedEventPayload,
    CustomPanelSelectionChangedEventPayload,
    CustomZShapedPanelSelectionChangedEventPayload,
    PanelSelectionChangedEventPayload,
} from "../eventBus/selectionChangedEventsPayloads";
import {
    FitToViewEventPayload,
    GenerationResultEventPayload,
    GenerateAllStartedEventPayload,
    ITypesManagerStartGenerationFailurePayload,
    TypesManagerGenerationFailureSourceEnum,
    PanelTypeSelectedEventPayload,
} from "../eventBus/typesManagerEventPayloads";
import eventBus, { IApplicationEvent } from "../eventBus/eventDispatcher";
import repo from "../../../Repository";

export type ForgeTypesManagerExtensionLoadOptions = {
    panelsEditor: PanelsModelEditor;
    cornersEditor: CornersModelEditor;
    customPanelsEditor: CustomPanelsModelEditor;
    customCornersEditor: CustomCornersModelEditor;
    customZShapedPanelsModelEditor: CustomZShapedPanelsModelEditor;
    model: Autodesk.Viewing.Model;
    generatedModels: IPanelGeneratedModelDto[];
};

export class ForgeTypesManagerExtension extends Autodesk.Viewing.Extension {
    private readonly panelsEditor: PanelsModelEditor;
    private readonly cornersEditor: CornersModelEditor;
    private readonly customPanelsEditor: CustomPanelsModelEditor;
    private readonly customCornersEditor: CustomCornersModelEditor;
    private readonly customZShapedPanelsEditor: CustomZShapedPanelsModelEditor;
    private readonly model: Autodesk.Viewing.Model;
    private readonly generatedModels: IPanelGeneratedModelDto[];
    private readonly categories: PanelTypeCategories;
    private readonly viewerZoomingService: ViewerZoomingService;
    private generatedPanelsContent: GeneratedPanelsContent;
    private dockingPanel: TypesManagerDockingPanel | null = null;
    private modelsGenerationButton: Autodesk.Viewing.UI.Button | null = null;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: ForgeTypesManagerExtensionLoadOptions) {
        super(viewer, options);

        this.panelsEditor = options.panelsEditor;
        this.cornersEditor = options.cornersEditor;
        this.customPanelsEditor = options.customPanelsEditor;
        this.customCornersEditor = options.customCornersEditor;
        this.customZShapedPanelsEditor = options.customZShapedPanelsModelEditor;
        this.model = options.model;
        this.generatedPanelsContent = new GeneratedPanelsContent(this.panelsEditor.facadeModelId);
        this.generatedModels = options.generatedModels;

        this.categories = new PanelTypeCategories(
            this.panelsEditor,
            this.cornersEditor,
            this.customPanelsEditor,
            this.customCornersEditor,
            this.customZShapedPanelsEditor,
        );

        this.viewerZoomingService = new ViewerZoomingService(this.viewer, this.model);

        this.onToggleTypesManager = this.onToggleTypesManager.bind(this);
        this.onDockingPanelVisibilityChanged = this.onDockingPanelVisibilityChanged.bind(this);
        this.turnOffTypesManager = this.turnOffTypesManager.bind(this);
        this.onSelectionChanged = this.onSelectionChanged.bind(this);
        this.onPanelSelectedFromUI = this.onPanelSelectedFromUI.bind(this);
        this.onPanelTypeSelected = this.onPanelTypeSelected.bind(this);
        this.onPanelSelectionChanged = this.onPanelSelectionChanged.bind(this);
        this.onCornerSelectionChanged = this.onCornerSelectionChanged.bind(this);
        this.onCustomPanelSelectionChanged = this.onCustomPanelSelectionChanged.bind(this);
        this.onCustomCornerSelectionChanged = this.onCustomCornerSelectionChanged.bind(this);
        this.onCustomZShapedPanelSelectionChanged = this.onCustomZShapedPanelSelectionChanged.bind(this);
        this.onSingleTypeGenerationRequested = this.onSingleTypeGenerationRequested.bind(this);
        this.onGenerateAllRequested = this.onGenerateAllRequested.bind(this);
        this.onUpdateGenerationTypes = this.onUpdateGenerationTypes.bind(this);
        this.onGenerationRequestFailed = this.onGenerationRequestFailed.bind(this);
        this.onPanelTypeGenerationStarted = this.onPanelTypeGenerationStarted.bind(this);
        this.onCornerPanelTypeGenerationStarted = this.onCornerPanelTypeGenerationStarted.bind(this);
        this.onGenerateAllStarted = this.onGenerateAllStarted.bind(this);
        this.onGenerationCompleted = this.onGenerationCompleted.bind(this);
        this.onPanelTypeHooksUpdate = this.onPanelTypeHooksUpdate.bind(this);
        this.onCornerTypeHooksUpdate = this.onCornerTypeHooksUpdate.bind(this);
    }

    load() {
        this.viewer.addEventListener(Autodesk.Viewing.ESCAPE_EVENT, this.turnOffTypesManager);
        this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.onSelectionChanged);

        eventBus.addEventListener("Dextall.TypesManager.SingleGeneration.Requested", this.onSingleTypeGenerationRequested);
        eventBus.addEventListener("Dextall.TypesManager.GenerateAll.Requested", this.onGenerateAllRequested);
        eventBus.addEventListener("Dextall.TypesManager.GenerationRequest.Failed", this.onGenerationRequestFailed);
        eventBus.addEventListener("Dextall.TypesManager.PanelType.SingleGeneration.Started", this.onPanelTypeGenerationStarted);
        eventBus.addEventListener("Dextall.TypesManager.CornerType.SingleGeneration.Started", this.onCornerPanelTypeGenerationStarted);
        eventBus.addEventListener("Dextall.TypesManager.GenerateAll.Started", this.onGenerateAllStarted);
        eventBus.addEventListener("Dextall.TypesManager.GenerationCompleted", this.onGenerationCompleted);

        eventBus.addEventListener("Dextall.TypesManager.PanelType.HooksUpdated", this.onPanelTypeHooksUpdate);
        eventBus.addEventListener("Dextall.TypesManager.CornerType.HooksUpdated", this.onCornerTypeHooksUpdate);

        eventBus.addEventListener("Dextall.Panels.SelectionChanged", this.onPanelSelectionChanged);
        eventBus.addEventListener("Dextall.Corners.SelectionChanged", this.onCornerSelectionChanged);
        eventBus.addEventListener("Dextall.CustomPanels.SelectionChanged", this.onCustomPanelSelectionChanged);
        eventBus.addEventListener("Dextall.CustomCorners.SelectionChanged", this.onCustomCornerSelectionChanged);
        eventBus.addEventListener("Dextall.CustomZShapedPanels.SelectionChanged", this.onCustomZShapedPanelSelectionChanged);

        eventBus.addEventListener("Dextall.TypesManager.UI.PanelSelected", this.onPanelSelectedFromUI);
        eventBus.addEventListener("Dextall.TypesManager.UI.PanelTypeSelected", this.onPanelTypeSelected);

        this.trackCurrentlyGeneratingPanels();

        return true;
    }

    unload() {
        this.viewer.removeEventListener(Autodesk.Viewing.ESCAPE_EVENT, this.turnOffTypesManager);
        this.viewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.onSelectionChanged);

        this.categories.clear();

        eventBus.removeEventListener("Dextall.TypesManager.SingleGeneration.Requested", this.onSingleTypeGenerationRequested);
        eventBus.removeEventListener("Dextall.TypesManager.GenerateAll.Requested", this.onGenerateAllRequested);
        eventBus.removeEventListener("Dextall.TypesManager.GenerationRequest.Failed", this.onGenerationRequestFailed);
        eventBus.removeEventListener("Dextall.TypesManager.PanelType.SingleGeneration.Started", this.onPanelTypeGenerationStarted);
        eventBus.removeEventListener("Dextall.TypesManager.CornerType.SingleGeneration.Started", this.onCornerPanelTypeGenerationStarted);
        eventBus.removeEventListener("Dextall.TypesManager.GenerateAll.Started", this.onGenerateAllStarted);
        eventBus.removeEventListener("Dextall.TypesManager.GenerationCompleted", this.onGenerationCompleted);

        eventBus.removeEventListener("Dextall.TypesManager.PanelType.HooksUpdated", this.onPanelTypeHooksUpdate);
        eventBus.removeEventListener("Dextall.TypesManager.CornerType.HooksUpdated", this.onCornerTypeHooksUpdate);

        eventBus.removeEventListener("Dextall.Panels.SelectionChanged", this.onPanelSelectionChanged);
        eventBus.removeEventListener("Dextall.Corners.SelectionChanged", this.onCornerSelectionChanged);
        eventBus.removeEventListener("Dextall.CustomPanels.SelectionChanged", this.onCustomPanelSelectionChanged);
        eventBus.removeEventListener("Dextall.CustomCorners.SelectionChanged", this.onCustomCornerSelectionChanged);
        eventBus.removeEventListener("Dextall.CustomZShapedPanels.SelectionChanged", this.onCustomZShapedPanelSelectionChanged);

        eventBus.removeEventListener("Dextall.TypesManager.UI.PanelSelected", this.onPanelSelectedFromUI);
        eventBus.removeEventListener("Dextall.TypesManager.UI.PanelTypeSelected", this.onPanelTypeSelected);

        this.dockingPanel?.shutdown();

        this.modelsGenerationButton?.removeEventListener("click", this.onToggleTypesManager);

        return true;
    }

    onToolbarCreated() {
        const toolbar = this.viewer.toolbar;

        let group = toolbar.getControl(hooksToolbarGroupId) as (Autodesk.Viewing.UI.ControlGroup | undefined);

        if (!group) {
            const measureTools = toolbar.getControl(
                measureToolsToolbarGroupId,
            ) as (Autodesk.Viewing.UI.ControlGroup | undefined);

            if (!measureTools) {
                toolbar.addControl(new Autodesk.Viewing.UI.ControlGroup(measureToolsToolbarGroupId), { index: 1 });
            }

            group = new Autodesk.Viewing.UI.ControlGroup(hooksToolbarGroupId);
            toolbar.addControl(group, { index: toolbar.indexOf(measureToolsToolbarGroupId) });
        }

        this.modelsGenerationButton = new Autodesk.Viewing.UI.Button("dextall-types-manager-button");
        this.modelsGenerationButton.setToolTip("Types Manager");
        this.modelsGenerationButton.setIcon("viewer-editor-types-manager");

        this.modelsGenerationButton.addEventListener("click", this.onToggleTypesManager);

        group.addControl(this.modelsGenerationButton);

        this.dockingPanel = new TypesManagerDockingPanel(this.viewer.container, this.onDockingPanelVisibilityChanged);
    }

    private updatePanelTypesWithGeneratedModels() {
        for (const model of this.generatedModels) {
            const panelType = this.categories.findReqularPanelTypeById(model.typeId);
            if (panelType) {
                panelType.generatedModel = model;
            }
        }
    }

    private trackCurrentlyGeneratingPanels() {
        this.updatePanelTypesWithGeneratedModels();

        const panelTypeIds: Set<string> = new Set();
        const cornerTypeIds: Set<string> = new Set();

        for (const model of this.generatedModels) {
            if (model.status === PanelTypeGenerationStatus.ModelGenerationInProgress) {
                const correspondingPanelType = this.panelsEditor.findPanelType(model.typeId);

                if (correspondingPanelType) {
                    panelTypeIds.add(model.typeId);
                } else {
                    cornerTypeIds.add(model.typeId);
                }
            }
        }

        if (panelTypeIds.size === 0 && cornerTypeIds.size === 0) {
            return;
        }

        const panelTypeIdsArray = Array.from(panelTypeIds);
        const cornerTypeIdsArray = Array.from(cornerTypeIds);

        this.generatedPanelsContent.trackUnprocessed(panelTypeIdsArray, cornerTypeIdsArray);
    }

    private selectPanel(
        panel: FacadeElement | undefined,
        panelType?: PanelType<IPanelSource | ICornerPanelSource>,
        isPanelModel = true,
    ) {
        if (!panel) {
            return;
        }

        this.categories.selectPanel(panel.id);

        if (panelType && panelType.generatedModel) {
            this.updateModelPreviewByPanelType(panelType, isPanelModel, false);
        }

        this.refresh(true);
    }

    private onPanelSelectionChanged(event: IApplicationEvent<PanelSelectionChangedEventPayload | null>) {
        const panel = event.payload?.panel;

        const panelType = panel && (panel.customPanelTypeId === null
            ? this.panelsEditor.findPanelType(panel.panelTypeId)
            : this.panelsEditor.findConvertedPanelType(panel.customPanelTypeId));

        this.selectPanel(panel, panelType, true);
    }

    private onCornerSelectionChanged(event: IApplicationEvent<CornerPanelSelectionChangedEventPayload | null>) {
        const corner = event.payload?.panel;
        const cornerType = corner ? this.cornersEditor.findCornerPanelType(corner.panelTypeId) : undefined;
        this.selectPanel(corner, cornerType, false);
    }

    private onCustomPanelSelectionChanged(event: IApplicationEvent<CustomPanelSelectionChangedEventPayload | null>) {
        this.selectPanel(event.payload?.panel);
    }

    private onCustomCornerSelectionChanged(event: IApplicationEvent<CustomCornerSelectionChangedEventPayload | null>) {
        this.selectPanel(event.payload?.panel);
    }

    private onCustomZShapedPanelSelectionChanged(
        event: IApplicationEvent<CustomZShapedPanelSelectionChangedEventPayload | null>,
    ) {
        this.selectPanel(event.payload?.panel);
    }

    private onSingleTypeGenerationRequested(event: IApplicationEvent<RequestModelGenerationPayload>) {
        const { panelTypeId, isPanelModel } = event.payload;

        const panelType = this.categories.findReqularPanelTypeById(panelTypeId);

        if (isPanelModel) {
            this.generatedPanelsContent.startSinglePanelTypeGeneration(panelTypeId);
        } else {
            this.generatedPanelsContent.startSingleCornerPanelTypeGeneration(panelTypeId);
        }

        if (panelType) {
            panelType.setInProgress();
            this.refresh(false);
        }
    }

    private onGenerateAllRequested() {
        this.panelsEditor.panelTypes.forEach(panelType => {
            if (panelType.status === PanelTypeGenerationStatus.None) {
                panelType.setInProgress();
            }
        });

        this.cornersEditor.panelTypes.forEach(cornerType => {
            if (cornerType.status === PanelTypeGenerationStatus.None) {
                cornerType.setInProgress();
            }
        });

        this.refresh(false);

        this.generatedPanelsContent.startMissingModelGenerations(this.panelsEditor.facadeModelId);
    }

    private updateGeneratedPanelTypes(panelModels: IPanelGeneratedModelDto[], refreshAfterUpdate = true) {
        for (const panelModel of panelModels) {
            const panelType = this.panelsEditor.panelTypes
                .find(type => type.generatedModel?.id === panelModel.id);

            if (panelType) {
                panelType.generatedModel = panelModel;
            }
        }

        if (refreshAfterUpdate) {
            this.refresh(true);
        }
    }

    private onUpdateGenerationTypes(panels: IPanelGeneratedModelDto[], corners: IPanelGeneratedModelDto[]) {
        this.updateGeneratedPanelTypes(panels, false);
        this.updateGeneratedPanelTypes(corners, false);

        this.refresh(true);
    }

    private onGenerationRequestFailed(event: IApplicationEvent<ITypesManagerStartGenerationFailurePayload>) {
        const { message, source } = event.payload;

        if (source.type === TypesManagerGenerationFailureSourceEnum.SinglePanelTypeGeneration
            || source.type === TypesManagerGenerationFailureSourceEnum.SingleCornerTypeGeneration) {
            const panelType = this.panelsEditor.findPanelType(source.id);

            panelType?.setFailed();
        }

        if (source.type === TypesManagerGenerationFailureSourceEnum.EntireModelGeneration) {
            const panelTypes = this.panelsEditor.panelTypes;
            const cornerTypes = this.cornersEditor.panelTypes;
            const allTypes = [...panelTypes, ...cornerTypes];

            for (const panelType of allTypes) {
                if (panelType.status === PanelTypeGenerationStatus.ModelGenerationInProgress) {
                    panelType.setFailed();
                    this.refresh(false);
                }
            }
        }

        eventBus.dispatchEvent({
            type: "Dextall.TypesManager.ShowFailureMessage",
            payload: message,
        });
    }

    private async findDrawings(
        model: IPanelGeneratedModelDto,
        isPanelModel: boolean,
    ): Promise<IPanelGeneratedDrawingDto[]> {
        if (model.status !== PanelTypeGenerationStatus.ModelGeneratedSuccessfully) {
            return Promise.resolve([]);
        }

        this.setDrawingsLoading(true, model.typeId);

        const response = isPanelModel
            ? await repo.findPanelTypeModelDrawings(model.id)
            : await repo.findCornerPanelTypeModelDrawings(model.id);

        this.setDrawingsLoading(false, model.typeId);

        return response.isSuccess ? response.items : [];
    }

    private setDrawingsLoading(isLoading: boolean, panelTypeId: string) {
        eventBus.dispatchEvent({
            type: "Dextall.TypesManager.UI.DrawingsLoadingStateChanged",
            payload: { isLoading, panelTypeId },
        });
    }

    private async updateModelPreviewByPanelType(
        panelType: PanelType<IPanelSource | ICornerPanelSource> | undefined,
        isPanelModel: boolean,
        setToInProgress: boolean,
    ) {
        if (!panelType) {
            return;
        }

        let model = panelType.generatedModel;

        if (setToInProgress) {
            model = model
                ? { ...model, status: PanelTypeGenerationStatus.ModelGenerationInProgress }
                : {
                    id: "",
                    bubble: null,
                    status: PanelTypeGenerationStatus.ModelGenerationInProgress,
                    typeId: panelType.id,
                };

            panelType.setInProgress();
        } else if (!model) {
            model = {
                id: "",
                bubble: null,
                status: PanelTypeGenerationStatus.None,
                typeId: panelType.id,
            };
        }

        let drawings = panelType.generatedDrawings;
        if (!drawings) {
            this.setDrawingsLoading(true, model.typeId);
            drawings = await this.findDrawings(model, isPanelModel);
        }

        if (!panelType.generatedDrawings && drawings.length > 0) {
            panelType.setGeneratedDrawings(drawings);
        }

        const payloadType = isPanelModel
            ? "Dextall.TypesManager.UI.PanelType.SetGeneratedModel"
            : "Dextall.TypesManager.UI.CornerType.SetGeneratedModel";

        eventBus.dispatchEvent({
            type: payloadType,
            payload: {
                model,
                panelTypeId: panelType.id,
                drawings,
            },
        });
    }

    private async updateGeneratedModelPreview(model: IPanelGeneratedModelDto, isPanelModel: boolean) {
        const panelType = isPanelModel
            ? this.panelsEditor.findPanelType(model.typeId)
            : this.cornersEditor.findCornerPanelType(model.typeId);

        const drawings = panelType?.generatedDrawings ?? await this.findDrawings(model, isPanelModel);
        if (!panelType?.generatedDrawings) {
            panelType?.setGeneratedDrawings(drawings);
        }

        const payloadType = isPanelModel
            ? "Dextall.TypesManager.UI.PanelType.SetGeneratedModel"
            : "Dextall.TypesManager.UI.CornerType.SetGeneratedModel";

        eventBus.dispatchEvent({
            type: payloadType,
            payload: {
                model,
                panelTypeId: panelType?.id ?? "",
                drawings,
            },
        });
    }

    private onPanelTypeGenerationStarted(event: IApplicationEvent<string>) {
        const panelTypeId = event.payload;

        const panelType = this.panelsEditor.findPanelType(panelTypeId);

        this.updateModelPreviewByPanelType(panelType, true, true);
    }

    private onCornerPanelTypeGenerationStarted(event: IApplicationEvent<string>) {
        const panelTypeId = event.payload;

        const panelType = this.cornersEditor.findCornerPanelType(panelTypeId);

        this.updateModelPreviewByPanelType(panelType, false, true);
    }

    private onGenerateAllStarted(event: IApplicationEvent<GenerateAllStartedEventPayload>) {
        const { panelTypeIds, cornerTypeIds } = event.payload;

        for (const panelTypeId of panelTypeIds) {
            const panelType = this.panelsEditor.findPanelType(panelTypeId);

            this.updateModelPreviewByPanelType(panelType, true, true);
        }

        for (const cornerTypeId of cornerTypeIds) {
            const cornerType = this.cornersEditor.findCornerPanelType(cornerTypeId);

            this.updateModelPreviewByPanelType(cornerType, false, true);
        }
    }

    private onGenerationCompleted(event: IApplicationEvent<GenerationResultEventPayload>) {
        const { panels, corners } = event.payload;

        for (const generatedPanelModel of panels) {
            this.updateGeneratedModelPreview(generatedPanelModel, true);
        }

        for (const generatedCornerModel of corners) {
            this.updateGeneratedModelPreview(generatedCornerModel, false);
        }

        this.onUpdateGenerationTypes(panels, corners);
    }

    private onPanelTypeHooksUpdate(event: IApplicationEvent<string>) {
        const panelTypeId = event.payload;
        this.generatedPanelsContent.abortCheck(panelTypeId, true);
    }

    private onCornerTypeHooksUpdate(event: IApplicationEvent<string>) {
        const panelTypeId = event.payload;
        this.generatedPanelsContent.abortCheck(panelTypeId, false);
    }

    private getAllPanelsForType(panelTypeId: string, panelCategory: CategoryType): FacadeElement[] {
        return this.categories.isCustomCategory(panelCategory)
            ? this.categories.findCustomPanelsByTypeId(panelTypeId, panelCategory)
            : this.categories.findRegularPanelsByTypeId(panelTypeId);
    }

    private onPanelSelectedFromUI(event: IApplicationEvent<FitToViewEventPayload>) {
        const { panelId } = event.payload;

        const panel = this.categories.findPanelById(panelId);
        if (!panel) {
            return;
        }

        this.categories.selectPanel(panel.id);
        this.viewerZoomingService.zoomToPanel(panel);
        this.refresh(false);
    }

    private onPanelTypeSelected(event: IApplicationEvent<PanelTypeSelectedEventPayload>) {
        const { panelTypeId, panelCategory } = event.payload;

        const panelType = this.categories.findPanelTypeById(panelTypeId);
        if (!panelType) {
            return;
        }

        const panels = this.getAllPanelsForType(panelTypeId, panelCategory);
        const firstPanel = panels[0];
        if (!firstPanel) {
            return;
        }

        this.categories.selectPanel(firstPanel.id);

        this.refresh(false);

        this.viewerZoomingService.zoomToPanel(firstPanel);
    }

    private refresh(updateCategories: boolean) {
        if (!this.dockingPanel?.visible) {
            return;
        }

        if (updateCategories) {
            this.categories.refresh();
        }

        eventBus.dispatchEvent({
            type: "Dextall.TypesManager.UI.Refresh",
            payload: {
                categories: this.categories.asArray(),
                selectedPanelId: this.categories.selectedPanelId,
            },
        });
    }

    private onToggleTypesManager() {
        const state = this.dockingPanel?.visible;

        if (state) {
            this.turnOffTypesManager();
        } else {
            this.dockingPanel?.setVisible(true);
            this.onDockingPanelVisibilityChanged(true);
            this.refresh(true);
        }
    }

    private onDockingPanelVisibilityChanged(visibility: boolean) {
        const newState = visibility
            ? Autodesk.Viewing.UI.Button.State.ACTIVE
            : Autodesk.Viewing.UI.Button.State.INACTIVE;

        this.modelsGenerationButton?.setState(newState);
    }

    private onSelectionChanged(event: IViewerAggregateSelectionChangedEventPayload) {
        const selection = event.selections[0];
        if (!selection) {
            this.categories.selectPanel(null);
            this.refresh(false);
            return;
        }
    }

    private turnOffTypesManager() {
        this.dockingPanel?.setVisible(false);
        this.onDockingPanelVisibilityChanged(false);
    }
}

export const forgeTypesManagerExtensionName = "Dextall.ForgeTypesManagerExtension" as const;

Autodesk.Viewing.theExtensionManager.registerExtension(forgeTypesManagerExtensionName, ForgeTypesManagerExtension);
